// Org-wide unified usage meters for /usage.
//
// As of 1 Jul 2026 the two usage counters — Overall Clients and Response
// Generation — are ONE org-wide limit each, spanning Income Tax + GST +
// TDS (not three per-portal caps). This renders those two meters from the
// `usage` block on /subscriptions/status. Bars use the shared quotaHelpers
// escalation (green/amber/red).

import { Card, Col, Progress, Row, Skeleton, Typography } from "antd";
import { quotaColor, quotaLabel, quotaPct } from "./quotaHelpers";

const { Title, Text } = Typography;

function QuotaRow({ label, used, max }) {
  const pct = quotaPct(used, max);
  const colour = quotaColor(used, max);
  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <Text strong>{label}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {quotaLabel(used, max)}
        </Text>
      </div>
      <Progress
        percent={pct}
        strokeColor={colour}
        showInfo={false}
        size="small"
      />
    </div>
  );
}

function UsageCard({ title, subtitle, used, max }) {
  return (
    <Card>
      <div className="mb-3">
        <Title level={5} style={{ margin: 0 }}>
          {title}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {subtitle}
        </Text>
      </div>
      <QuotaRow label="Used" used={used} max={max} />
    </Card>
  );
}

export default function PortalQuotaMeters({ status, isLoading }) {
  if (isLoading) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 4 }} />
      </Card>
    );
  }

  // Org-wide usage block (1 Jul 2026). Falls back to zeros so the meters
  // render even if the field is briefly absent (older cached response).
  const usage = status?.usage || {};

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12} key="clients">
        <UsageCard
          title="Overall Clients"
          subtitle="Across Income Tax, GST and TDS"
          used={usage.used_clients ?? 0}
          max={usage.max_clients ?? 0}
        />
      </Col>
      <Col xs={24} md={12} key="responses">
        <UsageCard
          title="Response Generation"
          subtitle="Across all portals (AI and legal share this pool)"
          used={usage.used_responses ?? 0}
          max={usage.max_responses ?? 0}
        />
      </Col>
    </Row>
  );
}
